import { getValidDiscounts } from '@web-platform/shared-universal-utils-frontend/src/helpers/paymentDiscounts';

import type {
  PaymentDiscountsBookingPanel,
  PaymentDiscountsStateBookingPanel,
} from '@web-platform/shared-universal-types/src/redux/state/shared/flightBookingPanel/entities/paymentDiscountsBookingPanel';

export const get = (
  state: PaymentDiscountsStateBookingPanel | undefined,
  id: string,
): PaymentDiscountsBookingPanel | undefined => state?.itineraries.byId[id];

export const getAvailable = (
  state: PaymentDiscountsStateBookingPanel | undefined,
  id: string,
): PaymentDiscountsBookingPanel | undefined => {
  const itineraryPaymentDiscounts = state?.itineraries.byId[id];

  return (
    itineraryPaymentDiscounts && {
      id: itineraryPaymentDiscounts.id,
      pricingOptions: itineraryPaymentDiscounts.pricingOptions.filter(
        (po) => po.status !== 'UNAVAILABLE',
      ),
    }
  );
};

export const getPaymentDiscountNames = (
  state: PaymentDiscountsStateBookingPanel | undefined,
  itineraryId: string,
  enablePaymentDiscounts: boolean,
): string[] => {
  const itinerary = getAvailable(state, itineraryId);

  if (!itinerary) {
    return [];
  }

  const names = new Set<string>();

  itinerary.pricingOptions.forEach((pricingOption) => {
    const validDiscounts = getValidDiscounts(
      enablePaymentDiscounts,
      pricingOption.paymentDiscounts,
    );

    validDiscounts?.forEach((discount) => {
      names.add(discount.discountProvider);
    });
  });

  return [...names].sort((a, b) => a.localeCompare(b));
};

export const getSelectedPaymentMethodFilter = (
  state: PaymentDiscountsStateBookingPanel | undefined,
): string[] | undefined => state?.selectedPaymentMethodFilter;

export const getPaymentDiscountTooltipDismissed = (
  state: PaymentDiscountsStateBookingPanel | undefined,
): boolean => state?.selectedTooltipDismissed ?? false;

// Not wrapped in createSelector: the .map() path creates new object references on every call.
// Only use this as an input to a createSelector factory (e.g. makeGet* in pricingOptionsBookingPanel);
// never call the state/index.ts re-export directly in component code.
export const getFilteredPaymentDiscounts = (
  state: PaymentDiscountsStateBookingPanel | undefined,
  itineraryId: string,
): PaymentDiscountsBookingPanel | undefined => {
  const itinerary = getAvailable(state, itineraryId);

  if (!itinerary) {
    return undefined;
  }

  const filter = getSelectedPaymentMethodFilter(state);

  if (filter === undefined) {
    return itinerary;
  }

  return {
    ...itinerary,
    pricingOptions: itinerary.pricingOptions.map((pricingOption) => ({
      ...pricingOption,
      paymentDiscounts: pricingOption.paymentDiscounts.filter((discount) =>
        filter.includes(discount.discountProvider),
      ),
    })),
  };
};
